import { Component, OnDestroy, OnInit, inject, input, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CropField } from '@next/arable-lib/crop-field';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  distinctUntilChanged,
  map,
  merge,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  throwError,
} from 'rxjs';

import { LoadingCardComponent } from 'src/app/shared/components/loading-card.component/loading-card.component';
import { CropVarietyService } from 'src/app/shared/services/crop-variety.service';
import { SiteState } from 'src/app/shared/models';
import { loadCropFields, loadCropFieldsFailure, loadCropFieldsSuccess } from 'src/app/tga/store/tga.actions';
import { selectCropFields } from 'src/app/tga/store/tga.selectors';

import { BiomassBenchmarkWidgetComponent } from '../../../shared/biomass-benchmark/tga-biomass-benchmark-widget.component';
import {
  BIOMASS_BENCHMARK_EVALUATION_DATASOURCE,
  BiomassBenchmarkEvaluationDatasource,
} from '../../../shared/biomass-benchmark/biomass-benchmark.datasource';
import {
  BiomassBenchmarkEvaluationCropFieldContext,
  BiomassBenchmarkEvaluationDatasourceState,
} from '../../../shared/biomass-benchmark/biomass-benchmark.models';

const BIOMASS_ERROR_STATE: BiomassBenchmarkEvaluationDatasourceState = {
  state: 'error',
  messageKey: 'TGA.Advice.Cultivation.BiomassBenchmark.Error',
};

@Component({
  selector: 'app-tga-biomass-evaluation',
  templateUrl: './tga-biomass-evaluation.component.html',
  imports: [BiomassBenchmarkWidgetComponent, LoadingCardComponent],
})
export class TgaBiomassEvaluationComponent implements OnInit, OnDestroy {
  readonly cropYear = input<number>(new Date().getFullYear());
  readonly dataState = signal<BiomassBenchmarkEvaluationDatasourceState>({ state: 'empty' });

  private readonly store = inject<Store<SiteState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly cropVarietyService = inject(CropVarietyService);
  private readonly datasource = inject<BiomassBenchmarkEvaluationDatasource>(BIOMASS_BENCHMARK_EVALUATION_DATASOURCE);
  private readonly cropYear$ = toObservable(this.cropYear);
  private readonly destroy$ = new Subject<void>();
  private useInitialCropFields = true;

  ngOnInit(): void {
    this.cropYear$
      .pipe(
        startWith(this.cropYear()),
        distinctUntilChanged(),
        switchMap((cropYear) =>
          this.loadCropFieldsForYear(cropYear).pipe(
            switchMap((cropFields) => this.createCropFieldContexts(cropFields)),
            switchMap((cropFields) => this.datasource.loadData({ cropYear, cropFields })),
            catchError(() => of<BiomassBenchmarkEvaluationDatasourceState>(BIOMASS_ERROR_STATE)),
            startWith<BiomassBenchmarkEvaluationDatasourceState>({ state: 'loading' }),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((state) => this.dataState.set(state));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCropFieldsForYear(cropYear: number) {
    return this.store.select(selectCropFields).pipe(
      take(1),
      switchMap((cropFields) => {
        if (this.useInitialCropFields && cropFields.length > 0) {
          this.useInitialCropFields = false;
          return of(cropFields);
        }

        this.useInitialCropFields = false;
        this.store.dispatch(loadCropFields({ cropYear }));

        const cropFieldsLoaded$ = this.actions$.pipe(
          ofType(loadCropFieldsSuccess),
          map((action) => action.payload.cropFields),
        );
        const cropFieldsFailed$ = this.actions$.pipe(
          ofType(loadCropFieldsFailure),
          switchMap((action) => throwError(() => action.payload.error)),
        );

        return merge(cropFieldsLoaded$, cropFieldsFailed$).pipe(take(1));
      }),
    );
  }

  private createCropFieldContexts(cropFields: Array<{ cropField: CropField }>) {
    const cropIds = [
      ...new Set(cropFields.map(({ cropField }) => cropField.cropId).filter((cropId): cropId is number => !!cropId)),
    ];
    if (cropIds.length === 0) {
      return of<BiomassBenchmarkEvaluationCropFieldContext[]>([]);
    }

    return this.cropVarietyService.getCropVarietiesByCropIds(cropIds).pipe(
      map((cropVarieties) => {
        const cropVarietyNames = new Map(cropVarieties.map((cropVariety) => [cropVariety.id, cropVariety.name]));

        return cropFields
          .map(({ cropField }) => {
            const cropVarietyId = cropField.cropVarietyId;
            if (!Number.isInteger(cropField.id) || !Number.isInteger(cropVarietyId) || cropVarietyId <= 0) {
              return null;
            }

            return {
              cropFieldId: cropField.id,
              cropVarietyId,
              cropFieldName: cropField.name,
              cropVarietyName: cropVarietyNames.get(cropVarietyId) ?? '',
            };
          })
          .filter((cropField): cropField is BiomassBenchmarkEvaluationCropFieldContext => cropField !== null);
      }),
    );
  }
}
